// dashboard/views/DashboardView.js
// The main patient-roster tab. Previously rendered as an inline
// `view === 'dashboard' && <JSX>}` block directly inside App() — since that
// block never unmounted across tab switches, it had no natural place to put
// a mount-time refetch, so patients/wellnessPatients only ever reflected
// whatever App()'s one fetch call fetched at login, until either a full
// page reload or the manual Refresh button. Now a real component: App()
// mounts it only while `view === 'dashboard'`, so switching back to this
// tab mounts a fresh instance and this effect fires, matching the pattern
// SupportView already used correctly. Data fetching/state ownership
// deliberately stays in App() (via the onMount callback) rather than moving
// here — patients/handleDismissAlert/handlePrintSummary are also used by
// PatientDetailPanel and Sidebar, which render outside
// this component's lifecycle. Extracted/added as part of the App.js split
// (2026-08-12); onMount was itself firing a second, concurrent copy of
// this same patients+wellness fetch until 2026-08-13 (see
// fetchGlobalData's comment in App.js) — fixed by giving this effect sole
// ownership of that data instead of overlapping with App()'s own
// initial-load fetch.

import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import i18n from '../../i18n';
import { c } from '../constants';
import FilterBar from '../FilterBar';
import PatientSection from '../patientList/PatientSection';
import WellnessSection from '../patientList/WellnessSection';
import ActivationChecklistView from './ActivationChecklistView';

export default function DashboardView({
  canClinical, canManage, loading, lastUpdated, onRefresh, onMount,
  filters, setFilters, vets, conditionTypes, profile, onSetDefault,
  patientsError, hasPatients, immediateGroups, redGroups, yellowGroups, recentlyReviewedGroups, greenGroups, mutedGroups, wellnessPatients, wellnessPatientsError,
  onSelect, onDismissAlert, onReactivateAlert, onPrintSummary, selectedPatient, useLbs, freezeCutoff, isInCatchUpWindow, recoveryThresholds,
  onReassignDoctor, onAssignCondition,
  clinic, subscription, subscriptionLoaded, refreshSubscription, pendingPets, setView,
}) {
  const { t } = useTranslation();

  useEffect(() => { onMount(); }, [onMount]);

  if (!canClinical && canManage) {
    // COA2 fix — a Manager-only founder IS the administrator; telling them
    // to "contact an administrator" (the branch below) is a dead end, not
    // a restriction notice. See ActivationChecklistView's own header
    // comment for the full rationale and done-state derivation.
    return (
      <ActivationChecklistView
        clinic={clinic} subscription={subscription} subscriptionLoaded={subscriptionLoaded}
        refreshSubscription={refreshSubscription} vets={vets} pendingPets={pendingPets} setView={setView}
      />
    );
  }

  if (!canClinical) {
    // Defensive fallback for a membership that is neither Doctor nor
    // Manager — not a normally-reachable state today (every clinic_members
    // row created by this app's own flows sets at least one of
    // is_doctor/is_manager), kept rather than removed in case that
    // invariant is ever relaxed.
    return (
      <div style={{ backgroundColor: c.white, borderRadius: 12, padding: 48, textAlign: 'center', color: c.textSoft, border: `1px solid ${c.border}` }}>
        <p style={{ fontSize: 15, fontWeight: 500, color: c.slate, marginBottom: 8 }}>{t('mainView.clinicalRestricted')}</p>
        <p style={{ fontSize: 13 }}>{t('mainView.contactAdmin')}</p>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: c.slate, margin: '0 0 4px' }}>{t('mainView.dashboardTitle')}</h1>
          <p style={{ fontSize: 13, color: c.textSoft, margin: 0 }}>{t('mainView.dashboardSubtitle')}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastUpdated && <span style={{ fontSize: 11, color: c.textSoft }}>{t('mainView.updatedAt', { time: lastUpdated.toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' }) })}</span>}
          <button
            onClick={onRefresh}
            disabled={loading}
            aria-busy={loading}
            style={{ padding: '7px 12px', border: `1px solid ${c.border}`, borderRadius: 8, background: 'none', fontSize: 12, color: c.textSoft, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.55 : 1 }}
          >
            {t('mainView.refresh')}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: c.textSoft }}>{t('mainView.loadingPatients')}</div>
      ) : (
        <>
          <FilterBar filters={filters} setFilters={setFilters} vets={vets} conditions={conditionTypes} profile={profile} onSetDefault={onSetDefault} />
          {patientsError && (
            <div style={{ backgroundColor: '#fff5f5', borderRadius: 12, padding: 48, textAlign: 'center', color: c.error, border: `1px solid ${c.error}33` }}>
              <p style={{ margin: '0 0 12px' }}>{t('mainView.couldNotLoadPatients')}</p>
              <button type="button" onClick={onRefresh} disabled={loading} style={{ padding: '7px 12px', border: `1px solid ${c.error}`, borderRadius: 8, backgroundColor: c.white, color: c.error, cursor: loading ? 'default' : 'pointer' }}>
                {t('common.retry')}
              </button>
            </div>
          )}
          {!hasPatients ? (
            !patientsError &&
            <div style={{ backgroundColor: c.white, borderRadius: 12, padding: 48, textAlign: 'center', color: c.textSoft, border: `1px solid ${c.border}` }}>
              {t('mainView.noActivePatients')}
            </div>
          ) : (
            <>
              <PatientSection title={t('mainView.sectionImmediateReview')} severity="immediate_review" petGroups={immediateGroups} onSelect={onSelect} onDismissAlert={onDismissAlert} onReactivateAlert={onReactivateAlert} onPrintSummary={onPrintSummary} selectedPatient={selectedPatient} defaultOpen={true} useLbs={useLbs} freezeCutoff={freezeCutoff} isInCatchUpWindow={isInCatchUpWindow} recoveryThresholds={recoveryThresholds} vets={vets} canClinical={canClinical} onReassignDoctor={onReassignDoctor} onAssignCondition={onAssignCondition} />
              <PatientSection title={t('mainView.sectionRed')}  severity="red"    petGroups={redGroups}    onSelect={onSelect} onDismissAlert={onDismissAlert} onReactivateAlert={onReactivateAlert} onPrintSummary={onPrintSummary} selectedPatient={selectedPatient} defaultOpen={true}  useLbs={useLbs} freezeCutoff={freezeCutoff} isInCatchUpWindow={isInCatchUpWindow} recoveryThresholds={recoveryThresholds} vets={vets} canClinical={canClinical} onReassignDoctor={onReassignDoctor} onAssignCondition={onAssignCondition} />
              <PatientSection title={t('mainView.sectionYellow')}    severity="yellow" petGroups={yellowGroups} onSelect={onSelect} onDismissAlert={onDismissAlert} onReactivateAlert={onReactivateAlert} onPrintSummary={onPrintSummary} selectedPatient={selectedPatient} defaultOpen={true}  useLbs={useLbs} freezeCutoff={freezeCutoff} isInCatchUpWindow={isInCatchUpWindow} recoveryThresholds={recoveryThresholds} vets={vets} canClinical={canClinical} onReassignDoctor={onReassignDoctor} onAssignCondition={onAssignCondition} />
              {/* Recently Reviewed (2026-09-08 correction; Dismiss/Snooze/
                  Acknowledge merged into one "Mark reviewed" action
                  2026-09-09): marking reviewed is a queue-position change
                  only, never a severity/color change (see docs/
                  ALERT_LIFECYCLE_REDESIGN) — cards here keep their true
                  severity color via each ConditionRow's own severity,
                  this section just groups them out of the active queue.
                  Collapsed by default, same as Inactive below — reviewed
                  patients shouldn't demand attention twice, but must stay
                  reachable via "Make Active Again". */}
              <PatientSection title={t('mainView.sectionRecentlyReviewed')} severity="muted" petGroups={recentlyReviewedGroups} onSelect={onSelect} onDismissAlert={onDismissAlert} onReactivateAlert={onReactivateAlert} onPrintSummary={onPrintSummary} selectedPatient={selectedPatient} defaultOpen={false} useLbs={useLbs} freezeCutoff={freezeCutoff} isInCatchUpWindow={isInCatchUpWindow} recoveryThresholds={recoveryThresholds} vets={vets} canClinical={canClinical} onReassignDoctor={onReassignDoctor} onAssignCondition={onAssignCondition} />
              <PatientSection title={t('mainView.sectionGreen')}      severity="green"  petGroups={greenGroups}  onSelect={onSelect} onDismissAlert={onDismissAlert} onReactivateAlert={onReactivateAlert} onPrintSummary={onPrintSummary} selectedPatient={selectedPatient} defaultOpen={false} useLbs={useLbs} freezeCutoff={freezeCutoff} isInCatchUpWindow={isInCatchUpWindow} recoveryThresholds={recoveryThresholds} vets={vets} canClinical={canClinical} onReassignDoctor={onReassignDoctor} onAssignCondition={onAssignCondition} />
              {/* Muted relationships (migration 183) — collapsed by default,
                  intentionally the only section with no clinician
                  notification: a clinic opted out of this patient's alerts,
                  so it should reorganize quietly, not demand attention. */}
              <PatientSection title={t('mainView.sectionInactive')}    severity="muted"  petGroups={mutedGroups} onSelect={onSelect} onDismissAlert={onDismissAlert} onReactivateAlert={onReactivateAlert} onPrintSummary={onPrintSummary} selectedPatient={selectedPatient} defaultOpen={false} useLbs={useLbs} freezeCutoff={freezeCutoff} isInCatchUpWindow={isInCatchUpWindow} recoveryThresholds={recoveryThresholds} vets={vets} canClinical={canClinical} onReassignDoctor={onReassignDoctor} onAssignCondition={onAssignCondition} />
            </>
          )}
          {/* Was inside the hasPatients ternary above -- a clinic with
              wellness-linked pets but zero disease-condition patients
              never saw this section at all, regardless of how many
              wellness patients it actually had. Wellness has its own
              independent fetch/error state, so it belongs outside that
              branch entirely. */}
          <WellnessSection wellnessPatients={wellnessPatients} error={wellnessPatientsError} />
        </>
      )}
    </>
  );
}
